import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { fmt } from '../utils/gst';
import { getInitialMonth } from '../utils/format';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const COLORS = ['#2D6BE4','#6D28D9','#1E7E4E','#D97706','#C0392B','#0369A1','#9D174D','#065F46'];

function RevenueOverview({ d }) {
  const base   = d.totalBase || 0;
  const gst    = d.totalGst  || 0;
  const billed = base + gst;

  return (
    <div className="card" style={{ marginBottom: 16, padding: '20px 24px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
        Revenue breakdown — this month
      </div>

      {/* 3 equal columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {/* Total Billed */}
        <div style={{ padding: '18px 20px', background: 'var(--surface)', borderRight: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Total Billed</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text1)', fontVariantNumeric: 'tabular-nums' }}>{fmt(billed)}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Incl. GST charged to clients</div>
        </div>

        {/* GST Output Tax */}
        <div style={{ padding: '18px 20px', background: '#FFF9EC', borderRight: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: '#92650A', marginBottom: 6, fontWeight: 600 }}>GST Output Tax</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#D97706', fontVariantNumeric: 'tabular-nums' }}>{fmt(gst)}</div>
          <div style={{ fontSize: 11, color: '#92650A', marginTop: 4 }}>Collected · remit to govt</div>
        </div>

        {/* Net Revenue */}
        <div style={{ padding: '18px 20px', background: 'var(--accent-light)' }}>
          <div style={{ fontSize: 11, color: 'var(--accent)', marginBottom: 6, fontWeight: 600 }}>Net Revenue</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>{fmt(base)}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{fmt(billed)} billed − {fmt(gst)} GST</div>
        </div>
      </div>
    </div>
  );
}

function QuarterlyGST() {
  const { data: qd } = useQuery({
    queryKey: ['quarterly-gst'],
    queryFn: () => api.get('/reports/quarterly').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  if (!qd) return null;

  const { fy, quarters, currentQIdx } = qd;
  const maxGst = Math.max(...quarters.map(q => q.gst), 1);
  const currentQ = quarters[currentQIdx];

  return (
    <div className="card" style={{ marginBottom: 16, padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Quarterly GST Liability — FY {fy}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
            Paid quarterly in India — plan your cash accordingly
          </div>
        </div>
        {currentQ && currentQ.gst > 0 && (
          <div style={{ textAlign: 'right', padding: '8px 14px', background: '#FFF3CD', borderRadius: 8, border: '1px solid #D97706' }}>
            <div style={{ fontSize: 10, color: '#92650A', fontWeight: 600 }}>{quarters[currentQIdx]?.name} due now</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#D97706' }}>{fmt(currentQ.gst)}</div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {quarters.map((q, i) => {
          const isCurrent = i === currentQIdx;
          const barWidth = q.gst > 0 ? Math.max(4, (q.gst / maxGst) * 100) : 0;
          return (
            <div key={q.name} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 68, flexShrink: 0 }}>
                <div style={{ fontSize: 11, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? 'var(--accent)' : 'var(--text2)' }}>
                  {q.name} {isCurrent && <span style={{ fontSize: 9, background: 'var(--accent)', color: '#fff', borderRadius: 3, padding: '1px 4px', marginLeft: 2 }}>now</span>}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>{q.label}</div>
              </div>
              <div style={{ flex: 1, height: 28, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                {q.gst > 0 ? (
                  <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: barWidth + '%',
                    background: isCurrent ? '#D97706' : 'var(--text3)',
                    borderRadius: 4,
                    opacity: isCurrent ? 1 : 0.45,
                    transition: 'width 0.6s ease',
                  }} />
                ) : null}
              </div>
              <div style={{ width: 100, textAlign: 'right', flexShrink: 0 }}>
                {q.gst > 0 ? (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? '#D97706' : 'var(--text2)' }}>{fmt(q.gst)}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>{fmt(q.base)} base</div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>—</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PnLSummary({ month, netRevenue }) {
  const { data: exp } = useQuery({
    queryKey: ['expense-summary', month, netRevenue],
    queryFn: () => api.get(`/expenses/summary?month=${month}&net_revenue=${netRevenue}`).then(r => r.data),
    staleTime: 60000,
    enabled: !!month,
  });

  if (!exp) return null;

  const { byCategory, totalExpenses, totalITC, salaryTotal, totalCashOut, monthlyTaxProvision } = exp;
  const grossProfit = netRevenue - totalCashOut;
  const netGstDue = (parseFloat(netRevenue > 0 ? netRevenue * 0 : 0)); // gst already in parent
  const netAfterTax = grossProfit - monthlyTaxProvision;

  if (totalCashOut === 0 && !byCategory?.length) {
    return (
      <div className="card" style={{ marginBottom: 16, padding: '16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Profit & Loss — this month</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>No expenses logged yet for this month</div>
          </div>
          <a href="/expenses" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>+ Add Expenses →</a>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 16, padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Profit & Loss — this month
        </div>
        <a href="/expenses" style={{ fontSize: 11, color: 'var(--accent)', textDecoration: 'none' }}>Manage expenses →</a>
      </div>

      {/* P&L rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span style={{ color: 'var(--text2)' }}>Net Revenue (excl. GST)</span>
          <span style={{ fontWeight: 600, color: 'var(--text1)' }}>{fmt(netRevenue)}</span>
        </div>
        {salaryTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: 'var(--text2)' }}>💰 Salaries (from payroll)</span>
            <span style={{ color: '#C0392B' }}>− {fmt(salaryTotal)}</span>
          </div>
        )}
        {byCategory.filter(c => c.total > 0).map(c => (
          <div key={c.cat} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: 'var(--text2)' }}>{c.icon} {c.cat}</span>
            <span style={{ color: '#C0392B' }}>− {fmt(c.total)}</span>
          </div>
        ))}
        {totalITC > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#1E7E4E' }}>
            <span>ITC (input tax credit on expenses)</span>
            <span>+ {fmt(totalITC)}</span>
          </div>
        )}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700 }}>
          <span>Gross Profit</span>
          <span style={{ color: grossProfit >= 0 ? '#1E7E4E' : '#C0392B' }}>{fmt(grossProfit)}</span>
        </div>
        {monthlyTaxProvision > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: 'var(--text2)' }}>Income tax provision (annualized slab)</span>
            <span style={{ color: '#C0392B' }}>− {fmt(monthlyTaxProvision)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 800, background: grossProfit >= 0 ? 'var(--accent-light)' : '#FFF0F0', padding: '10px 12px', borderRadius: 6, marginTop: 4 }}>
          <span style={{ color: grossProfit >= 0 ? 'var(--accent)' : '#C0392B' }}>Net Profit (after tax provision)</span>
          <span style={{ color: grossProfit >= 0 ? 'var(--accent)' : '#C0392B' }}>{fmt(netAfterTax)}</span>
        </div>
      </div>

      {/* Margin indicator */}
      {netRevenue > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 16 }}>
          <span>Margin: <strong style={{ color: grossProfit / netRevenue >= 0.3 ? '#1E7E4E' : '#D97706' }}>{((grossProfit / netRevenue) * 100).toFixed(1)}%</strong></span>
          <span>Total cash out: <strong>{fmt(totalCashOut)}</strong></span>
          {totalITC > 0 && <span>ITC offset: <strong>{fmt(totalITC)}</strong></span>}
        </div>
      )}
    </div>
  );
}

function UnbilledAlert() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['unbilled-alerts'],
    queryFn: () => api.get('/reports/unbilled-alerts').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });
  if (!data || data.clientCount === 0) return null;
  const urgent = data.maxDays >= 30;
  return (
    <div onClick={() => navigate('/receivables?tab=unbilled')} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 16px', borderRadius: 8, marginBottom: 14, cursor: 'pointer',
      background: urgent ? '#FFF0F0' : '#FFFBEB',
      border: `1px solid ${urgent ? '#C0392B' : '#D97706'}`,
    }}>
      <span style={{ fontSize: 13, color: urgent ? '#C0392B' : '#D97706', fontWeight: 600 }}>
        {urgent ? '🔴' : '⚠️'} {data.clientCount} client{data.clientCount > 1 ? 's' : ''} {' '}
        ha{data.clientCount > 1 ? 've' : 's'} unbilled work — oldest is {data.maxDays} days ago
      </span>
      <span style={{ fontSize: 12, color: urgent ? '#C0392B' : '#D97706', fontWeight: 700 }}>Review & Invoice →</span>
    </div>
  );
}

export default function DashboardPage() {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  const [month, setMonth] = useState(getInitialMonth);

  useEffect(() => {
    const handler = e => setMonth(e.detail);
    window.addEventListener('monthChange', handler);
    return () => window.removeEventListener('monthChange', handler);
  }, []);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dashboard', month],
    queryFn: () => api.get(`/reports/dashboard?month=${month}`).then(r => r.data),
    refetchInterval: 30000,
  });

  useEffect(() => { refetch(); }, [month]);

  if (isLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  const d = data || {};

  return (
    <div>
      <div className="section-head">
        <h2>Dashboard — {new Date(month + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}</h2>
      </div>

      {/* Unbilled work alert — owner only */}
      {isOwner && <UnbilledAlert />}

      {/* Revenue Overview — owner only */}
      {isOwner && <RevenueOverview d={d} />}

      {/* Secondary KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        {isOwner
          ? <div className="kpi"><div className="kpi-label">Total jobs</div><div className="kpi-val">{d.totalJobs || 0}</div><div className="kpi-sub">All team, this month</div></div>
          : <div className="kpi"><div className="kpi-label">My jobs this month</div><div className="kpi-val">{d.myJobs || 0}</div><div className="kpi-sub">Out of {d.totalJobs||0} total</div></div>
        }
        {isOwner && <div className="kpi"><div className="kpi-label">Correction revenue</div><div className="kpi-val">{fmt(d.totalCorrections || 0)}</div><div className="kpi-sub">This month</div></div>}
        <div className="kpi"><div className="kpi-label">Active clients</div><div className="kpi-val">{d.activeClients || 0}</div></div>
        {isOwner && <div className="kpi"><div className="kpi-label">Corrections flagged</div><div className="kpi-val" style={{ color: d.correctionCount > 0 ? 'var(--red)' : undefined }}>{d.correctionCount || 0}</div></div>}
        {isOwner && <div className="kpi"><div className="kpi-label">Duplicates</div><div className="kpi-val" style={{ color: d.duplicateCount > 0 ? 'var(--orange)' : undefined }}>{d.duplicateCount || 0}</div><div className="kpi-sub">{d.duplicateCount > 0 ? <button className="btn-ghost btn-sm" onClick={() => navigate('/worklog')}>Review →</button> : 'All clear'}</div></div>}
      </div>

      {/* Quarterly GST — owner only */}
      {isOwner && <QuarterlyGST />}

      {/* P&L — owner only */}
      {isOwner && <PnLSummary month={month} netRevenue={d.totalBase || 0} />}

      {isOwner && d.byClient && d.byCategory && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="card">
            <div className="card-title">Revenue by client</div>
            <div style={{ height: 200 }}>
              <Bar
                data={{ labels: d.byClient.map(c => c.name), datasets: [{ data: d.byClient.map(c => c.base), backgroundColor: COLORS, borderRadius: 4 }] }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 10 } } }, y: { ticks: { callback: v => '₹' + v.toLocaleString('en-IN'), font: { size: 10 } }, grid: { color: 'rgba(0,0,0,.05)' }, border: { display: false } } } }}
              />
            </div>
          </div>
          <div className="card">
            <div className="card-title">Jobs by category</div>
            <div style={{ height: 200 }}>
              <Doughnut
                data={{ labels: d.byCategory.map(c => c.name), datasets: [{ data: d.byCategory.map(c => c.jobs), backgroundColor: COLORS, borderWidth: 0 }] }}
                options={{ responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { position: 'right', labels: { font: { size: 10 }, boxWidth: 10 } } } }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="section-head"><h2>{isOwner ? 'Clients this month' : 'My work this month — by client'}</h2></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {isOwner && (d.byClient || []).map((c, i) => (
          <div key={c.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', cursor: 'pointer' }}
            onClick={() => navigate('/visuals')}>
            <div className="avatar" style={{ width: 36, height: 36, fontSize: 12, background: COLORS[i % COLORS.length] + '22', color: COLORS[i % COLORS.length] }}>
              {c.name.slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{c.jobs} jobs · {fmt(c.base + c.corrections)}</div>
            </div>
            <button className="btn-primary btn-sm" onClick={e => { e.stopPropagation(); navigate('/report'); }}>Invoice</button>
          </div>
        ))}
        {!isOwner && (d.myByClient || []).map((c, i) => (
          <div key={c.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px' }}>
            <div className="avatar" style={{ width: 36, height: 36, fontSize: 12, background: COLORS[i % COLORS.length] + '22', color: COLORS[i % COLORS.length] }}>
              {c.name.slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{c.jobs} jobs logged by me</div>
            </div>
          </div>
        ))}
        {isOwner && (!d.byClient || !d.byClient.length) && <div className="empty">No work logged for this month yet</div>}
        {!isOwner && (!d.myByClient || !d.myByClient.length) && <div className="empty">You haven't logged any work for this month yet</div>}
      </div>
    </div>
  );
}
